import { useState } from 'react'
import { router } from '@inertiajs/react'
import { PendingInvitation, ROLE_LABELS, UserRole } from '../../types'

interface Props {
  invitation: PendingInvitation
  canManage: boolean
}

export default function PendingInvitationRow({ invitation, canManage }: Props) {
  const [resending, setResending] = useState(false)

  const handleResend = (e: React.MouseEvent) => {
    e.stopPropagation()
    setResending(true)
    router.post(`/invitations/${invitation.id}/resend`, {}, {
      onFinish: () => setResending(false),
    })
  }

  const handleRevoke = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Revoke this invitation?')) {
      router.delete(`/invitations/${invitation.id}`)
    }
  }

  const daysUntilExpiry = Math.max(0, Math.ceil(
    (new Date(invitation.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  ))

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-stone/10 last:border-0 bg-[#B87A20]/5">
      <div className="w-8 h-8 rounded-full bg-stone/20 flex items-center justify-center shrink-0">
        <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary truncate">{invitation.email}</span>
          <span className="text-[12px] font-semibold bg-[#B87A20]/20 text-[#B87A20] px-1.5 py-0.5 rounded-full uppercase tracking-wide">Pending</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-sm text-text-secondary">Invited by {invitation.invited_by_name}</span>
          <span className="text-text-muted">·</span>
          <span className="text-sm text-text-secondary">
            {daysUntilExpiry > 0 ? `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}` : 'Expired'}
          </span>
        </div>
      </div>

      <span className="text-sm text-text-secondary bg-stone/20 px-2 py-0.5 rounded-full shrink-0">
        {ROLE_LABELS[invitation.role as UserRole] || invitation.role}
      </span>

      {canManage && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-sm text-text-primary font-medium px-2 py-1 rounded hover:bg-charcoal/10 transition-colors disabled:opacity-50"
          >
            {resending ? 'Sending...' : 'Resend'}
          </button>
          <button
            onClick={handleRevoke}
            className="text-sm text-pinnacle-red hover:text-pinnacle-red/80 font-medium px-2 py-1 rounded hover:bg-pinnacle-red/10 transition-colors"
          >
            Revoke
          </button>
        </div>
      )}
    </div>
  )
}
